/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { uniqueId } from 'lodash';
import { FlexLayout, FlexItem, ToggleSwitchSection } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';

import ApplyButton from './ApplyButton';
import ConditionBuilder from './ConditionBuilder';
import FilterHeader from './FilterHeader';
import SelectValueFilterPane from './SelectValueFilterPane';

import './CompactNonNumericFilter.scss';

export default class CompactNonNumericFilter extends Component<any, any> {
	// Prop definitions
	static propTypes = {
		title: PropTypes.string.isRequired,
		menuTitle: PropTypes.string,
		totalValues: PropTypes.number.isRequired,
		selectedValuesCount: PropTypes.number,
		loading: PropTypes.bool,
		onClick: PropTypes.func.isRequired,
		onChange: PropTypes.func.isRequired,
		// onInvert: PropTypes.func.isRequired,
		// onClear: PropTypes.func.isRequired,
		onSearch: PropTypes.func,
		onApply: PropTypes.func.isRequired,
		contentRenderer: PropTypes.func,
		getValue: PropTypes.func.isRequired,
		condition: PropTypes.object,
		allowSearch: PropTypes.bool,
		showCondition: PropTypes.bool,
		allowCondition: PropTypes.bool,
		inverted: PropTypes.bool,
		onClear: PropTypes.func,
		isDirty: PropTypes.bool,
		isSearching: PropTypes.bool,
		showToggle: PropTypes.bool,
		onToggleChange: PropTypes.func,
		isToggled: PropTypes.bool,

		// Non-API props
		showHeader: PropTypes.bool,
		readOnly: PropTypes.bool,
		conditionFlyoutContentContainerId: PropTypes.string,
		hasMoreData: PropTypes.bool,
	};

	static defaultProps = {
		title: '',
		selectedValuesCount: 0,
		loading: false,
		showHeader: true,
		inverted: false,
		showCondition: undefined,
		readOnly: false,
		allowCondition: true,
		allowSearch: true,
		hasMoreData: false,
		showToggle: false,
		isToggled: false,
	};

	state = {
		isOpen: false,
		showSearch: this.props.allowSearch, // this hides/shows the search input
		isDirty: false,
		// these states are used to determine dirty
		// these values are set in _resetDirty()
		selectedValuesCount: undefined,
		inverted: undefined,
	};

	componentWillMount() {
		this._conditionFlyoutContentContainerId = uniqueId('filter-condition-');
		this._resetDirty();
	}

	componentWillReceiveProps(nextProps) {
		this.setState({
			isDirty:
				this.state.selectedValuesCount !== nextProps.selectedValuesCount ||
				this.state.inverted !== nextProps.inverted,
		});
	}

	getConditionFlyoutContentContainerId = () => {
		const id = this.props.conditionFlyoutContentContainerId;

		if (typeof id === 'undefined' || id === null || id === '') {
			return this._conditionFlyoutContentContainerId;
		}

		return id;
	};

	_conditionFlyoutContentContainerId: string;

	_resetDirty = () => {
		this.setState({
			isDirty: false,
			selectedValuesCount: this.props.selectedValuesCount,
			inverted: this.props.inverted,
		});
	};

	_onSearchClick = () => {
		this.setState(prevState => ({ showSearch: !prevState.showSearch }));
	};

	_onEditFilter = () => {
		this.setState({ isOpen: true });
	};

	_onInvert = () => {
		const { onChange } = this.props;

		onChange && onChange({ inverted: !this.props.inverted });
	};

	_onClearAll = () => {
		const { onClear } = this.props;

		onClear && onClear();
	};

	_onOpenCondition = () => {
		this.setState({ isOpen: true });
	};

	_onCloseCondition = () => {
		this.setState({ isOpen: false });
	};

	_shouldShowCondition = () => {
		const { showCondition, allowCondition } = this.props;

		if (showCondition !== undefined && showCondition !== null) {
			return showCondition; // legacy, takes precedence
		}

		return allowCondition;
	};

	_getContextMenu = () => {
		const menu = [];

		menu.push({ label: stringResource.get('invert'), fn: this._onInvert });
		menu.push({ label: stringResource.get('clearAll'), fn: this._onClearAll });

		if (this.state.isOpen !== true && this._shouldShowCondition()) {
			const { condition } = this.props;
			const label =
				condition && condition.predicates
					? stringResource.get('editCondition')
					: stringResource.get('newCondition');

			menu.push({ label, fn: this._onOpenCondition });
		}

		return menu;
	};

	_onApply = () => {
		const { onApply } = this.props;

		onApply && onApply();
		this._resetDirty();
	};

	_renderCondition() {
		const { isOpen } = this.state;
		const { condition, onChange, readOnly } = this.props;

		if (isOpen) {
			return (
				<FlexItem padding={[0, 0, 0, 0]}>
					<ConditionBuilder
						condition={condition}
						onChange={onChange}
						open={isOpen}
						toggleSlideout={this._onCloseCondition}
						showToggleButton={false}
						height={0}
						readOnly={readOnly}
						conditionFlyoutContentContainerId={this.getConditionFlyoutContentContainerId()}
					/>
				</FlexItem>
			);
		}

		return null;
	}

	render() {
		const { showSearch } = this.state;
		// TODO add onInvert and onClear
		const {
			condition,
			showHeader,
			title,
			menuTitle,
			loading,
			selectedValuesCount,
			totalValues,
			onSearch,
			onChange,
			onClick,
			getValue,
			contentRenderer,
			readOnly,
			allowSearch,
			hasMoreData,
			isSearching,
			showToggle,
		} = this.props;

		const borderStyle = {
			'border-color': '#F2F2F2',
			'border-width': '1px',
			'border-style': 'solid',
		};

		return (
			<FlexLayout fullHeight className="bi-filter-select-value" direction="column">
				{showHeader === true && (
					<FlexItem gutter={[0, 2, 0, 2]}>
						<FilterHeader
							title={title}
							menuTitle={menuTitle}
							onSearch={this._onSearchClick}
							menu={this._getContextMenu()}
							loading={loading}
							height={40}
							readOnly={readOnly}
							showSearch={allowSearch}
						/>
					</FlexItem>
				)}

				{showToggle && (
					<FlexItem gutter={[3, 3, 0, 3]}>
						<ToggleSwitchSection
							label={stringResource.get('allMembers')}
							checked={this.props.isToggled}
							onChange={this.props.onToggleChange}
						/>
					</FlexItem>
				)}

				{this._shouldShowCondition() && !loading && this._renderCondition()}
				<FlexItem grow margin={[1, 2, 2, 2]} style={borderStyle}>
					<SelectValueFilterPane
						condition={condition}
						totalValues={totalValues}
						selectedValuesCount={selectedValuesCount}
						loading={loading}
						allowSearch={allowSearch && showSearch}
						onClick={onClick}
						onChange={onChange}
						onSearch={onSearch}
						isSearching={isSearching}
						getValue={getValue}
						contentRenderer={contentRenderer}
						showFilterPreferences={false}
						readOnly={readOnly}
						hasMoreData={hasMoreData}
					/>
				</FlexItem>
				<FlexItem textAlign="center">
					<ApplyButton
						dirty={
							this.props.isDirty === true || this.props.isDirty === false
								? this.props.isDirty
								: this.state.isDirty
						}
						onApply={this._onApply}
						loading={loading}
						readOnly={readOnly}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}
}
